/**
 * Tier-based permission utilities for frontend.
 * Maps tier names to feature access based on pricing packages.
 */

// Tier hierarchy (used for comparisons)
const TIER_HIERARCHY = {
  core: 1,
  complete: 2,
  pro: 3,
  premium: 4,
  elite: 5,
};

// Feature to minimum tier mapping
const FEATURE_TIER_MAP = {
  // Core features (all tiers)
  core_content: 'core',
  modules: 'core',
  labs: 'core',
  capstones: 'core',
  progress_tracking: 'core',
  downloadable_resources: 'core',
  standard_support: 'core',
  
  // Complete tier and above
  certificates: 'complete',
  module_certificates: 'complete',
  future_updates: 'complete',
  
  // Pro tier and above
  priority_support: 'pro',
  code_review: 'pro',
  
  // Premium tier and above
  premium_templates: 'premium',
  exclusive_case_studies: 'premium',
  vip_discord: 'premium',
  advanced_workshops: 'premium',
  
  // Elite tier only
  coaching_sessions: 'elite',
  direct_mentor_access: 'elite',
  career_strategy: 'elite',
  portfolio_review: 'elite',
};

/**
 * Check if a user's tier has access to a specific feature.
 * @param {string} userTier - The user's enrolled tier
 * @param {string} feature - The feature name to check
 * @returns {boolean} - True if user has access
 */
export function hasFeatureAccess(userTier, feature) {
  if (!userTier) return false;
  
  const requiredTier = FEATURE_TIER_MAP[feature];
  if (!requiredTier) return false;
  
  const userTierLevel = TIER_HIERARCHY[userTier] || 0;
  const requiredTierLevel = TIER_HIERARCHY[requiredTier] || 0;
  
  return userTierLevel >= requiredTierLevel;
}

/**
 * Check if user can access certificates.
 * @param {string} userTier - The user's enrolled tier
 * @returns {boolean}
 */
export function canAccessCertificates(userTier) {
  return hasFeatureAccess(userTier, 'certificates');
}

/**
 * Check if user can access priority support.
 * @param {string} userTier - The user's enrolled tier
 * @returns {boolean}
 */
export function canAccessPrioritySupport(userTier) {
  return hasFeatureAccess(userTier, 'priority_support');
}

/**
 * Check if user can access premium resources.
 * @param {string} userTier - The user's enrolled tier
 * @returns {boolean}
 */
export function canAccessPremiumResources(userTier) {
  return hasFeatureAccess(userTier, 'premium_templates');
}

/**
 * Check if user can access coaching.
 * @param {string} userTier - The user's enrolled tier
 * @returns {boolean}
 */
export function canAccessCoaching(userTier) {
  return hasFeatureAccess(userTier, 'coaching_sessions');
}

/**
 * Get all features available to a user based on their tier.
 * @param {string} userTier - The user's enrolled tier
 * @returns {object} - Object with feature names as keys and boolean access as values
 */
export function getAvailableFeatures(userTier) {
  const features = {};
  
  for (const [feature, requiredTier] of Object.entries(FEATURE_TIER_MAP)) {
    features[feature] = hasFeatureAccess(userTier, feature);
  }
  
  return features;
}

/**
 * Get the display name for a tier.
 * @param {string} tier - The tier code
 * @returns {string} - The display name
 */
export function getTierDisplayName(tier) {
  const displayNames = {
    core: 'Core Access',
    complete: 'Complete Package',
    pro: 'Pro Bundle',
    premium: 'Premium Complete',
    elite: 'Elite Mentorship',
  };
  
  return displayNames[tier] || 'No Tier';
}

/**
 * Get the required tier for a feature.
 * @param {string} feature - The feature name
 * @returns {string|null} - The minimum required tier
 */
export function getRequiredTier(feature) {
  return FEATURE_TIER_MAP[feature] || null;
}

/**
 * Get a user-friendly upgrade message for a feature.
 * @param {string} feature - The feature name
 * @returns {string} - The upgrade message
 */
export function getUpgradeMessage(feature) {
  const messages = {
    certificates: 'Certificate generation requires the Complete Package or higher. Upgrade to earn professional certificates!',
    module_certificates: 'Module certificates require the Complete Package or higher.',
    future_updates: 'Access to future course updates requires the Complete Package or higher.',
    priority_support: 'Priority support with 24-hour response time requires the Pro Bundle or higher.',
    code_review: 'Code review for capstones requires the Pro Bundle or higher.',
    premium_templates: 'Premium templates require the Premium Complete tier.',
    exclusive_case_studies: 'Exclusive case studies require the Premium Complete tier.',
    vip_discord: 'VIP Discord community access requires the Premium Complete tier.',
    advanced_workshops: 'Advanced workshops require the Premium Complete tier.',
    coaching_sessions: '1-on-1 coaching sessions are available exclusively with the Elite Mentorship tier.',
    direct_mentor_access: 'Direct mentor access is available exclusively with the Elite Mentorship tier.',
    career_strategy: 'Career strategy sessions are available exclusively with the Elite Mentorship tier.',
    portfolio_review: 'Portfolio review is available exclusively with the Elite Mentorship tier.',
  };
  
  return messages[feature] || 'This feature requires a higher tier. Please upgrade to continue.';
}

/**
 * Get all tiers that have access to a feature.
 * @param {string} feature - The feature name
 * @returns {string[]} - Array of tier names
 */
export function getTiersWithAccess(feature) {
  const requiredTier = FEATURE_TIER_MAP[feature];
  if (!requiredTier) return [];
  
  const requiredLevel = TIER_HIERARCHY[requiredTier];
  
  return Object.keys(TIER_HIERARCHY)
    .filter(tier => TIER_HIERARCHY[tier] >= requiredLevel);
}

/**
 * Compare two tiers.
 * @param {string} tier1 - First tier
 * @param {string} tier2 - Second tier
 * @returns {number} - Negative if tier1 < tier2, 0 if equal, positive if tier1 > tier2
 */
export function compareTiers(tier1, tier2) {
  const level1 = TIER_HIERARCHY[tier1] || 0;
  const level2 = TIER_HIERARCHY[tier2] || 0;
  
  return level1 - level2;
}

/**
 * Check if user needs to upgrade for a feature.
 * @param {string} userTier - The user's current tier
 * @param {string} feature - The feature to check
 * @returns {boolean} - True if upgrade is needed
 */
export function needsUpgrade(userTier, feature) {
  return !hasFeatureAccess(userTier, feature);
}

/**
 * Get the next tier that would unlock a feature.
 * @param {string} userTier - The user's current tier
 * @param {string} feature - The feature to check
 * @returns {string|null} - The next tier name or null if already has access
 */
export function getUpgradeTier(userTier, feature) {
  if (hasFeatureAccess(userTier, feature)) {
    return null;
  }
  
  return getRequiredTier(feature);
}

export default {
  hasFeatureAccess,
  canAccessCertificates,
  canAccessPrioritySupport,
  canAccessPremiumResources,
  canAccessCoaching,
  getAvailableFeatures,
  getTierDisplayName,
  getRequiredTier,
  getUpgradeMessage,
  getTiersWithAccess,
  compareTiers,
  needsUpgrade,
  getUpgradeTier,
};
